import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
    detail: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      const response = await fetch('https://readdy.ai/api/form/d6h41chtp4hj31tchv70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', category: '', subject: '', message: '', detail: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* 和紙風背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-slate-800 to-stone-900" />
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/fccab9e36afaa3a7fff3d4497cc41ec2.jpg')`,
        }}
      />

      {/* 上部装飾ライン */}
      <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-80" />
      <div className="absolute top-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

      {/* 市松模様装飾 */}
      <div className="absolute top-8 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-60`} />
        ))}
      </div>

      {/* 背景装飾円 */}
      <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-[#c9a84c]/10" />
      <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-[#c9a84c]/10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ヘッダー */}
        <div className="text-center mb-14">
          <span
            className="inline-flex items-center gap-2 bg-[#8b2500]/80 text-[#c9a84c] text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-4 border border-[#c9a84c]/30"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            <i className="ri-mail-line text-base"></i>
            Contact
          </span>
          <h2
            className="text-4xl sm:text-5xl font-extrabold text-white mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: '0.05em' }}
          >
            <span className="text-[#c9a84c]">◆</span>　お問い合わせ　<span className="text-[#c9a84c]">◆</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]" />
            <span className="text-[#c9a84c] text-sm">✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]" />
          </div>
          <p className="text-lg text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            ご質問・ご予約のお問い合わせはこちらからどうぞ
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* 左：アクセス情報 */}
          <div className="flex flex-col gap-6">
            {/* アクセスカード */}
            <div className="bg-white/8 backdrop-blur-sm border-2 border-[#c9a84c]/30 rounded-2xl p-7 relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

              <h3
                className="text-lg font-bold text-white mb-6 flex items-center gap-3"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl flex-shrink-0 shadow-lg">
                  <i className="ri-map-pin-2-line text-[#c9a84c] text-lg"></i>
                </div>
                <span>所在地・アクセス</span>
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#8b2500]/60 rounded-lg flex-shrink-0 border border-[#c9a84c]/30">
                    <i className="ri-map-pin-line text-[#c9a84c] text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#c9a84c] mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>住所</p>
                    <p className="text-sm text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>東京都大田区東馬込1-12-12</p>
                    <p className="text-sm text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>〒143-0022</p>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#8b2500]/60 rounded-lg flex-shrink-0 border border-[#c9a84c]/30">
                    <i className="ri-train-line text-[#c9a84c] text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#c9a84c] mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>アクセス</p>
                    <p className="text-sm text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>馬込駅より徒歩2分</p>
                    <p className="text-sm text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>羽田空港より約25分</p>
                  </div>
                </div>
              </div>

              {/* 信頼バッジ */}
              <div className="grid grid-cols-3 gap-3 mt-6">
                {[
                  { icon: 'ri-shield-check-line', label: '個人情報\n厳重管理' },
                  { icon: 'ri-time-line', label: '24時間\n以内返信' },
                  { icon: 'ri-phone-line', label: '電話相談\n可能' },
                ].map((item, i) => (
                  <div key={i} className="bg-[#8b2500]/40 border border-[#c9a84c]/30 rounded-xl p-3 text-center">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                      <i className={`${item.icon} text-[#c9a84c] text-lg`}></i>
                    </div>
                    <p className="text-[10px] text-slate-300 whitespace-pre-line leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 直接問い合わせボタン */}
            <div className="bg-white/8 backdrop-blur-sm border-2 border-[#c9a84c]/30 rounded-2xl p-6 relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

              <h3
                className="text-sm font-bold text-[#c9a84c] mb-1 flex items-center gap-2"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                <i className="ri-chat-3-line text-base"></i>
                すぐに相談したい方はこちら
              </h3>
              <p className="text-xs text-slate-400 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                チャットで気軽にお問い合わせいただけます
              </p>

              <div className="flex flex-col gap-3">
                {/* WhatsApp */}
                <a
                  href="https://wa.me/819092895522?text=%E3%81%93%E3%82%93%E3%81%AB%E3%81%A1%E3%81%AF%E3%80%82%E3%81%8A%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B%E3%81%97%E3%81%9F%E3%81%84%E3%81%AE%E3%81%A7%E3%81%99%E3%81%8C%E3%80%81%E3%82%88%E3%82%8D%E3%81%97%E3%81%84%E3%81%A7%E3%81%97%E3%82%87%E3%81%86%E3%81%8B%EF%BC%9F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3.5 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-green-900/40 cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-whatsapp-line text-white text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>WhatsAppで問い合わせ</p>
                    <p className="text-xs text-green-100 mt-0.5" style={{ fontFamily: "'Noto Serif JP', serif" }}>+81 90-9289-5522</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/70 text-base flex-shrink-0"></i>
                </a>

                {/* LINE */}
                <a
                  href="https://line.me/ti/p/yokota0809"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3.5 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-[#00B900]/30 cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #00C300 0%, #00B900 100%)' }}
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-line-fill text-white text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>LINEで問い合わせ</p>
                    <p className="text-xs text-green-100 mt-0.5" style={{ fontFamily: "'Noto Serif JP', serif" }}>ID: yokota0809</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/70 text-base flex-shrink-0"></i>
                </a>
              </div>

              <p className="text-[10px] text-slate-500 text-center mt-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                ※ 返信は通常数時間以内です
              </p>
            </div>

            {/* 地図 */}
            <div className="rounded-2xl overflow-hidden border-2 border-[#c9a84c]/40 shadow-xl h-[260px] relative">
              <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-[#c9a84c] z-10" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-[#c9a84c] z-10" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-[#c9a84c] z-10" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-[#c9a84c] z-10" />
              <iframe
                src="https://maps.google.com/maps?q=東京都大田区東馬込1-12-12&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          </div>

          {/* 右：フォーム */}
          <div
            className="rounded-2xl shadow-2xl p-8 md:p-10 border-4 border-[#8b2500] relative"
            style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 50%, #fff8e1 100%)' }}
          >
            <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#c9a84c]" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#c9a84c]" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#c9a84c]" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#c9a84c]" />

            <h3
              className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl shadow-lg">
                <i className="ri-send-plane-line text-[#c9a84c] text-lg"></i>
              </div>
              お問い合わせフォーム
            </h3>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              {/* 名前・メール */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>お名前</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>メールアドレス</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@email.com"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>
              </div>

              {/* 件名 */}
              <div>
                <label
                  htmlFor="subject"
                  className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  <span>件名</span>
                  <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c9a84c] rounded-l-lg" />
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="ご予約・お問い合わせについて"
                    className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  />
                </div>
              </div>

              {/* メッセージ */}
              <div>
                <label
                  htmlFor="contact-message"
                  className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  <span>お問い合わせ内容</span>
                  <div className="flex items-center gap-2">
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                    <span className={`text-[10px] ${formData.message.length > 500 ? 'text-red-500' : 'text-slate-400'}`}>
                      {formData.message.length}/500
                    </span>
                  </div>
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    required
                    placeholder="ご質問・ご要望などをご記入ください（500文字以内）"
                    className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all resize-none text-sm bg-white"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  />
                </div>
              </div>

              {/* 区切り線 */}
              <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

              {/* ステータスメッセージ */}
              {status === 'success' && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border-2 border-emerald-400">
                  <i className="ri-checkbox-circle-line text-lg text-emerald-600 flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-emerald-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    お問い合わせを受け付けました。24時間以内にご返信いたします。
                  </p>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border-2 border-red-400">
                  <i className="ri-error-warning-line text-lg text-red-600 flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-red-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    送信に失敗しました。お手数ですが、もう一度お試しください。
                  </p>
                </div>
              )}

              {/* 送信ボタン */}
              <button
                type="submit"
                disabled={status === 'submitting' || formData.message.length > 500}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-lg font-bold text-white transition-all hover:shadow-xl hover:shadow-[#8b2500]/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg whitespace-nowrap"
                style={{
                  background: 'linear-gradient(90deg, #8b2500, #a52a00, #8b2500)',
                  fontFamily: "'Noto Serif JP', serif",
                }}
              >
                <span className="text-[#c9a84c]">✦</span>
                <i className="ri-mail-send-line text-lg"></i>
                {status === 'submitting' ? '送信中...' : '送信する'}
                <span className="text-[#c9a84c]">✦</span>
              </button>

              <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

              <p className="text-center text-xs text-slate-500" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                ご確認後、担当者よりご連絡いたします
              </p>

              {/* お支払い方法 */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 100%)', border: '2px solid #c9a84c' }}
              >
                <p
                  className="text-xs font-bold text-center mb-3"
                  style={{ color: '#8b2500', fontFamily: "'Noto Serif JP', serif" }}
                >
                  ◆ ご利用可能なお支払い方法 ◆
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <span className="font-extrabold text-sm tracking-tight" style={{ color: '#1A1F71', fontFamily: 'Arial, sans-serif' }}>VISA</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <div className="w-5 h-5 rounded-full" style={{ background: '#EB001B' }}></div>
                    <div className="w-5 h-5 rounded-full -ml-2.5" style={{ background: '#F79E1B', opacity: 0.9 }}></div>
                  </div>
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <span className="font-extrabold text-sm" style={{ background: 'linear-gradient(135deg, #003087, #009f6b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'Arial, sans-serif' }}>JCB</span>
                  </div>
                  <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                    <span className="font-extrabold text-xs tracking-wide" style={{ color: '#007BC1', fontFamily: 'Arial, sans-serif' }}>AMEX</span>
                  </div>
                </div>
                <p className="text-xs text-center mt-2" style={{ color: '#9a7a50', fontFamily: "'Noto Serif JP', serif" }}>
                  クレジットカード決済に対応しています
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* 下部装飾ライン */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-60`} />
        ))}
      </div>
      <div className="absolute bottom-4 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-80" />
    </section>
  );
}
